import { useEffect, useRef, useState } from "react";
import { GuideLink } from "./GuideLink";
import { Link } from "./router";
import { withSurfaceBase } from "./surface-base";
import { readClientId } from "./personal";
import { refusalText } from "./sign-in-doors";
import { BrandLockup } from "./BrandLockup";
import { deviceAlreadyEnrolled, enrolPasskey, passkeysAvailable, type PasskeyDoor } from "./usePasskeys";
import { PASSWORD_MINIMUM_LENGTH } from "@verity-connect/domain";

export type SignInLinkAudience = "organizer" | "presenter" | "school";

type LinkState =
  | { status: "ready" }
  | { status: "missing" }
  | { status: "signing-in" }
  | { status: "signed-in" }
  | { status: "failed"; message: string };

const SIGN_IN_PATH: Record<SignInLinkAudience, string> = {
  organizer: "/",
  presenter: "/",
  school: "/",
};
const PASSKEY_DOOR: Record<SignInLinkAudience, PasskeyDoor> = {
  organizer: "organizer",
  presenter: "presenter",
  school: "school",
};

/**
 * The token arrives in the URL fragment, which browsers never send to a server.
 * A mail scanner that follows the link therefore loads this page without the
 * token and cannot spend it, and pressing the button is what completes sign-in.
 */
export function SignInLinkPage({ audience }: { audience: SignInLinkAudience }) {
  const token = useRef<string | null>(readFragmentToken());
  const [state, setState] = useState<LinkState>(token.current ? { status: "ready" } : { status: "missing" });

  useEffect(() => {
    // Keep the token out of the address bar, history, and any screenshot.
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  const signIn = async () => {
    const value = token.current;
    if (!value) return;
    setState({ status: "signing-in" });
    try {
      const response = await fetch(`/api/${audience}/auth/link`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        // Every door sends it now, because signing in at any door also opens a guide
        // session, and that session should name the same browser the rest of the app
        // already knows.
        body: JSON.stringify({ token: value, clientId: readClientId() }),
      });
      if (!response.ok) throw new Error(await refusalText(response));
      // A full load rather than a client-side push, so every part of the app
      // starts from the new session rather than the signed-out one.
      setState({ status: "signed-in" });
    } catch (reason) {
      token.current = null;
      setState({ status: "failed", message: message(reason) });
    }
  };

  return (
    <section className="organizer-auth-card" aria-labelledby="sign-in-link-title">
      <BrandLockup className="brand-lockup-card" />
      <p className="organizer-kicker">
        {audience === "organizer" ? "Authorized WVCEA staff" : audience === "presenter" ? "Convention presenter" : "Member school"}
      </p>
      <h1 id="sign-in-link-title" data-page-heading tabIndex={-1}>
        {state.status === "failed"
          ? "This link no longer works"
          : state.status === "signed-in"
            ? "You're signed in"
            // Nothing is being finished when there is no token to finish it with, and a
            // heading that says otherwise over a body saying the opposite is the screen
            // arguing with itself in front of somebody who came here to be let in.
            : state.status === "missing" ? "Sign in" : "Finish signing in"}
      </h1>

      {state.status === "missing" && (
        // Reached by following a link that carries no token — an invitation used to send
        // everybody here. Sign-in needs no link at all now: the door asks for an address and
        // answers with a password, so this says where to go rather than describing a fault
        // in a link the reader did not make and cannot repair.
        <p>Sign in with your email address instead — there is nothing to wait for.</p>
      )}
      {state.status === "failed" && <p className="organizer-form-error">{state.message}</p>}
      {(state.status === "ready" || state.status === "signing-in") && (
        <p>
          {audience === "organizer"
            ? "You are about to sign in to the WVCEA Connect staff workspace on this device."
            : audience === "presenter"
              ? "You are about to sign in to your presenter pages on this device."
              : "You are about to sign in to your school workspace on this device."}
        </p>
      )}

      {state.status === "signed-in" && (
        <FinishSetUp
          audience={audience}
          onFailed={(text) => setState({ status: "failed", message: text })}
        />
      )}

      {(state.status === "ready" || state.status === "signing-in") && (
        <button
          className="organizer-primary"
          type="button"
          disabled={state.status === "signing-in"}
          onClick={() => void signIn()}
        >
          {state.status === "signing-in" ? "Signing in…" : "Sign in"}
        </button>
      )}

      {state.status === "missing" && (
        <Link className="organizer-primary" href="/" fromRoot>Go to sign in</Link>
      )}
      {state.status === "failed" && (
        <Link className="organizer-primary" href={SIGN_IN_PATH[audience]}>Sign in again</Link>
      )}

      {state.status !== "missing" && (
        <p>
          A sign-in link works once. If you did not ask to sign in, close this page and nothing
          happens.
        </p>
      )}
      <GuideLink className="organizer-public-link">Return to the public convention guide</GuideLink>
    </section>
  );
}

function readFragmentToken(): string | null {
  const hash = window.location.hash.replace(/^#/u, "");
  const value = new URLSearchParams(hash).get("t");
  return value && /^[A-Za-z0-9_-]{16,}$/u.test(value) ? value : null;
}

function message(reason: unknown): string {
  return reason instanceof Error ? reason.message : "This sign-in link could not be used.";
}

/** Doors that can set a password from a session in hand. */
const PASSWORD_PATH: Partial<Record<SignInLinkAudience, string>> = {
  organizer: "/api/organizer/password",
  presenter: "/api/presenter/password",
};

/**
 * What somebody does before leaving this page, so that getting back in does not mean
 * waiting for another email.
 *
 * A password is offered first and to everybody, because it is the one that works on any
 * device. The passkey sits beside it where the device can hold one. Neither is required —
 * they are already signed in — but leaving with neither is what made the last visit's link
 * the only way in.
 */
function FinishSetUp({ audience, onFailed }: {
  audience: SignInLinkAudience;
  onFailed: (message: string) => void;
}) {
  const passwordPath = PASSWORD_PATH[audience];
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const short = password.length > 0 && password.length < PASSWORD_MINIMUM_LENGTH;
  const onward = () => window.location.assign(withSurfaceBase(SIGN_IN_PATH[audience]));

  const save = async () => {
    if (!passwordPath) return;
    setSaving(true);
    try {
      const response = await fetch(passwordPath, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!response.ok) throw new Error(await refusalText(response));
      setSaved(true);
    } catch (reason) {
      onFailed(message(reason));
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="join-form">
        <p className="sign-in-saved" role="status">
          Your password is set. Next time, sign in with your email address and that password.
        </p>
        <button className="organizer-primary" type="button" onClick={onward}>Continue</button>
      </div>
    );
  }

  return (
    <div className="join-form">
      {passwordPath && (
        <>
          <label htmlFor="sign-in-new-password">
            Choose a password
            <small>At least {PASSWORD_MINIMUM_LENGTH} characters. You will use this and your email address from now on.</small>
          </label>
          <input
            id="sign-in-new-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          {short && <p className="organizer-form-error">That is shorter than {PASSWORD_MINIMUM_LENGTH} characters.</p>}
          <button
            className="organizer-primary"
            type="button"
            disabled={saving || password.length < PASSWORD_MINIMUM_LENGTH}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save password"}
          </button>
        </>
      )}

      {passkeysAvailable() && (
        <div className="join-device-offer">
          <strong>Or use this device</strong>
          <p>Face ID, Touch ID, a fingerprint, or your screen lock. You can set up both.</p>
          <button
            className="sign-in-alternative"
            type="button"
            onClick={() => void enrolPasskey(PASSKEY_DOOR[audience]).then(onward)
              .catch((reason) => deviceAlreadyEnrolled(reason) ? onward() : onFailed(message(reason)))}
          >
            Set up device sign-in
          </button>
        </div>
      )}

      <button className="sign-in-alternative" type="button" onClick={onward}>Not now</button>
    </div>
  );
}
